import React, { useEffect, useState } from "react";
import { useTitle } from "../context/TitleContext";
import PermissionButton from "../components/PermissionButton";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSections,
  deleteSection,
  clearSectionState,
} from "../redux/Slices/SectionSlice";
import { toast } from "react-toastify";
import Pagination from "../components/Pagination";
import AddSection from "../components/Sections/AddSection";
import EditSection from "../components/Sections/EditSection";

const Sections = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const {
    data: sections,
    meta,
    loading,
    error,
    deleteLoading,
  } = useSelector((state) => state.sections);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSection, setDeletingSection] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setTitle("الأقسام");
    dispatch(clearSectionState());
  }, [setTitle, dispatch]);

  useEffect(() => {
    dispatch(fetchSections(currentPage));
  }, [dispatch, currentPage]);

  const handleDelete = (section) => {
    setDeletingSection(section);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingSection) {
      dispatch(deleteSection(deletingSection.id));
      setShowDeleteModal(false);
      setDeletingSection(null);
    }
  };

  const handleEdit = (section) => {
    dispatch(clearSectionState());
    setEditingSection(section);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
  };

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };



  useEffect(() => {
    if (error && error !== null) {
      toast.error(error);
      dispatch(clearSectionState());
    }
  }, [error, dispatch]);


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (loading && (!sections || sections.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .section-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .section-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .section-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            📂 الأقسام والشُعب الدراسية
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل ومراقبة وإضافة الأقسام العلمية (علمي، أدبي، لغات...) والربط مع الصفوف</p>
        </div>
        <PermissionButton
          permission="create-sections"
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingSection(null);
          }}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          {showAddForm ? "إخفاء النموذج" : "إضافة قسم جديد"}
        </PermissionButton>
      </div>

      {showAddForm && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 24, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
          <AddSection onSuccess={() => setShowAddForm(false)} />
        </div>
      )}

      {editingSection && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 24, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
          <EditSection
            section={editingSection}
            onSuccess={handleCancelEdit}
          />
        </div>
      )}

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!sections || sections.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <i className="bi bi-folder display-4 text-muted"></i>
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد أقسام مسجلة حالياً</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="section-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الاسم العربي</th>
                    <th>الاسم الإنجليزي</th>
                    <th>الصف الدراسي</th>
                    <th>الحالة</th>
                    <th>تاريخ الإنشاء</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section, index) => (
                    <tr key={section.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>{section.name_ar}</td>
                      <td style={{ color: "#475569" }}>{section.name_en}</td>
                      <td>
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>{section.grade_name_ar}</span>
                        {section.grade_name_en && (
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>{section.grade_name_en}</div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: section.status === "active" ? "#15803d" : "#64748b", background: section.status === "active" ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {section.status === "active" ? "نشط" : "غير نشط"}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: "#64748b" }}>{formatDate(section.created_at)}</td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => handleEdit(section)}
                            permission="edit-sections"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(section)}
                            permission="delete-sections"
                            disabled={deleteLoading}
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingSection(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذا القسم؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                📂 {deletingSection?.name_ar} ({deletingSection?.name_en})
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه وسيلغي انتساب كافة الطلاب المقيدين بهذا القسم.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingSection(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={deleteLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {deleteLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف القسم"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sections;
